import React from 'react';
import Icon from '../../../components/AppIcon';

const ProgressionPanel = ({ progressionData }) => {
  const { currentTier, winsRequired, nextTierWins, totalWins, scalingFactor } = progressionData;

  const tierColors = {
    1: 'accent',
    2: 'primary',
    3: 'secondary',
    4: 'warning',
    5: 'error'
  };

  const currentColor = tierColors?.[currentTier] || 'primary';

  return (
    <div className="progression-panel bg-card border-2 border-primary rounded-sm p-4 md:p-6 lg:p-8 shadow-glow-cyan-md">
      <div className="flex items-center gap-3 mb-4 md:mb-6">
        <div className={`w-10 h-10 md:w-12 md:h-12 rounded-sm bg-${currentColor}/20 border border-${currentColor} flex items-center justify-center shadow-glow-cyan-sm`}>
          <Icon name="TrendingUp" size={24} color={`var(--color-${currentColor})`} />
        </div>
        <div>
          <h2 className="font-heading font-bold text-lg md:text-xl lg:text-2xl text-primary">
            Progression System
          </h2>
          <p className="text-xs md:text-sm text-muted-foreground font-caption">
            Escalating Difficulty Mechanics
          </p>
        </div>
      </div>

      <div className="space-y-4 md:space-y-6">
        <div className="bg-background rounded-sm border border-primary/30 p-4 md:p-5">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm md:text-base font-caption text-foreground">Current Tier</span>
            <span className={`text-2xl md:text-3xl font-heading font-bold text-${currentColor}`}>
              {currentTier}
            </span>
          </div>
          <div className="w-full bg-muted rounded-sm h-2 md:h-3 overflow-hidden">
            <div 
              className={`h-full bg-${currentColor} transition-all duration-base`}
              style={{ width: `${(totalWins / nextTierWins) * 100}%` }}
            />
          </div>
          <div className="flex items-center justify-between mt-2 text-xs md:text-sm text-muted-foreground font-data">
            <span>{totalWins} wins</span>
            <span>{nextTierWins} for next tier</span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
          <div className="bg-background rounded-sm border border-accent/30 p-3 md:p-4">
            <div className="flex items-center gap-2 mb-2">
              <Icon name="Target" size={18} color="var(--color-accent)" />
              <span className="text-xs md:text-sm font-caption text-muted-foreground">
                Wins Required
              </span>
            </div>
            <p className="text-xl md:text-2xl font-heading font-bold text-accent">
              {winsRequired}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Hard wins for 1 gem
            </p>
          </div>

          <div className="bg-background rounded-sm border border-warning/30 p-3 md:p-4">
            <div className="flex items-center gap-2 mb-2">
              <Icon name="Activity" size={18} color="var(--color-warning)" />
              <span className="text-xs md:text-sm font-caption text-muted-foreground">
                Scaling Factor
              </span>
            </div>
            <p className="text-xl md:text-2xl font-heading font-bold text-warning">
              {scalingFactor}x
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Difficulty multiplier
            </p>
          </div>
        </div>

        <div className="bg-background rounded-sm border border-secondary/30 p-4 md:p-5">
          <h3 className="font-heading font-semibold text-sm md:text-base text-secondary mb-3 flex items-center gap-2">
            <Icon name="Info" size={18} />
            How It Works
          </h3>
          <ul className="space-y-2 text-xs md:text-sm text-muted-foreground font-caption">
            <li className="flex items-start gap-2">
              <Icon name="ChevronRight" size={16} className="mt-0.5 flex-shrink-0" color="var(--color-primary)" />
              <span>Win 2-3 hard matches to earn 1 gem at current tier</span>
            </li>
            <li className="flex items-start gap-2">
              <Icon name="ChevronRight" size={16} className="mt-0.5 flex-shrink-0" color="var(--color-primary)" />
              <span>Each tier increases wins required by scaling factor</span>
            </li>
            <li className="flex items-start gap-2">
              <Icon name="ChevronRight" size={16} className="mt-0.5 flex-shrink-0" color="var(--color-primary)" />
              <span>Higher tiers unlock rarer gem types and AI moods</span>
            </li>
            <li className="flex items-start gap-2">
              <Icon name="ChevronRight" size={16} className="mt-0.5 flex-shrink-0" color="var(--color-primary)" />
              <span>Progression resets never occur - all gems are permanent</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ProgressionPanel;